<!--
  Model browse page for the `service/:serviceKey/model/:modelKey` route.

  Two things separate it from the Tabulator page it replaces
  (views/service/TabulatorPages/PageServiceModelBrowse.vue, still used by the
  `dev-model` routes):

    1. The table is BreadDataTable (v-data-table) instead of BreadTabulator.
       The model files are untouched — BreadDataTable adapts their Tabulator
       column definitions, so formatters, cellClick, contextMenu and clickMenu
       keep working as written.
    2. The page chrome follows services/hrdc/views/application/Index.vue:
       a toolbar with the title and primary action, a collapsible filter panel,
       and the search bar on its own row.

  Inline cell editing is deliberately gone. BreadDataTable has no cell editors,
  and no model reaching this route declares one — every column on internalUser
  and internalRole is `editable: false`, so the old "Enable Cell Edit" toggle
  was already inert. A model that genuinely needs editing must stay on the
  `dev-model` route; the adapter logs a warning if a column declares an editor.
-->
<template>
  <v-container fluid class="tb-layout-browse1 rv-page">
    <ATabulatorLoder :isLoading="isLoading" />
    <AError :api="apiThatReturnedError" />

    <v-card v-if="canBrowse" class="pa-2 rv-card-wrapper">
      <!-- Page header -->
      <div class="page-toolbar">
        <div class="page-toolbar__left">
          <h1 class="page-toolbar__title">{{ model.name.plural }}</h1>
          <span id="record-count" class="page-toolbar__count"></span>
        </div>
        <div class="page-toolbar__right">
          <button
            class="new-app-btn"
            :disabled="!canAdd"
            @click="goToAdd()"
          >
            <v-icon size="16" color="#fff" class="mr-1">mdi-plus</v-icon>
            New {{ model.name.singular }}
          </button>
          <v-btn-toggle v-if="showUpload || showExport" dense multiple class="mr-1">
            <BreadTabulatorUpload
              v-if="showUpload"
              ref="excelUpload"
              :model="model"
            ></BreadTabulatorUpload>
            <BreadTabulatorExport
              v-if="showExport"
              ref="exportData"
              :model="model"
              :apiUrl="apiGetBrowseUrl()"
              @downloadData="downloadData"
            ></BreadTabulatorExport>
          </v-btn-toggle>
        </div>
      </div>

      <!-- Filter toggle -->
      <div class="filter-bar">
        <button class="filter-bar__toggle" @click="isShow = !isShow">
          <v-icon size="16" color="var(--app-text-muted)" class="mr-1">mdi-filter-variant</v-icon>
          <span>Filter</span>
          <span v-if="conditions.length > 0" class="filter-bar__badge">{{ conditions.length }}</span>
          <v-icon size="16" color="var(--app-text-faint)" class="ml-1">{{ isShow ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </button>
      </div>

      <!-- Collapsible filter panel -->
      <div v-show="isShow" class="filter-panel">
        <BreadTabulatorFilterable
          ref="filter"
          :model="model"
          :setConditions="setConditions"
        ></BreadTabulatorFilterable>

        <div class="filter-panel__columns">
          <BreadTabulatorSelectColumn
            ref="selectColumn"
            :model="model"
            @applyColumnSelection="applyColumnSelection"
          ></BreadTabulatorSelectColumn>
        </div>
      </div>

      <!-- Search bar -->
      <div class="page-search">
        <BreadTabulatorSearchable
          ref="search"
          :model="model"
          :setConditions="setConditions"
        ></BreadTabulatorSearchable>
      </div>

      <BreadTabulatorViewLatest
        ref="customView"
        :model="model"
        :selectedCustomColums="selectedCustomColums"
        :selectedAdvanceFilterConditions="selectedAdvanceFilterConditions"
        :selectedFilterConditions="selectedFilterConditions"
        :cColumnSort="cColumnSort"
        :displayRowCount="displayRowCount"
        :listAllViews="listAllViews"
        :allResizeColumns="allResizeColumns"
        @changeTableDetails="changeTableDetails"
      ></BreadTabulatorViewLatest>

      <div class="table-scroll-wrapper">
        <BreadDataTable
          ref="tabulatorDataTable"
          layout="fitColumns"
          :autoColumns="false"
          :paginationSize="paginationSize"
          :paginationSizeSelector="paginationSizeSelector"
          :apiUrl="apiGetBrowseUrl()"
          :columns="columns"
          :modelKey="modelKey"
          :deferInitialLoad="true"
          :columnSelectionDisabledCount="columnSelectionDisabledCount"
          @getCurrentPageSize="getCurrentPageSize"
          @getCurrentSort="getCurrentSort"
          @getCurrentPageNumber="getCurrentPageNumber"
          @getCurrentColumnWidth="getCurrentColumnWidth"
          @getSelectedRows="getSelectedRows"
          @getDataLoadErrorCode="getDataLoadErrorCode"
        ></BreadDataTable>
      </div>
    </v-card>

    <v-card v-else class="pa-2 rv-card-wrapper">
      <div class="rv-denied">
        <v-icon size="20" color="#dc2626" class="mr-2">mdi-alert-circle-outline</v-icon>
        You don't have permission to browse this model.
      </div>
    </v-card>

    <!-- Add, edit and delete all happen over the listing. Mounting the edit and
         delete ones is also what tells a row's action button there is a dialog
         here to open — without a host it routes to those pages as it always
         has. -->
    <BreadModelAddDialog ref="addDialog" @created="onRecordUpdated" />
    <BreadModelEditDialog @updated="onRecordUpdated" />
    <BreadModelDeleteDialog @deleted="onRecordUpdated" />
  </v-container>
</template>

<script>
import Api from "@/objects/api";
import BreadcrumbTabulator from "@/objects/breadcrumbTabulator";
import Model from "@/objects/model";
import Service from "@/objects/service";
import BreadModelAddDialog from "@/components/common/BreadModelAddDialog.vue";
import BreadModelDeleteDialog from "@/components/common/BreadModelDeleteDialog.vue";
import BreadModelEditDialog from "@/components/common/BreadModelEditDialog.vue";
import BreadTabulatorUpload from "@/components/common/BreadTabulatorUpload.vue";
import { mapState } from "vuex";

export default {
  components: {
    BreadModelAddDialog,
    BreadModelDeleteDialog,
    BreadModelEditDialog,
    BreadTabulatorUpload,
  },
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
      services: (state) => state.service.data.services,
      rolePermission: (state) => state.rolePermission.data,
    }),
    /**
     * Looked up defensively rather than indexed straight into. The old page
     * read `rolePermission[key].isAllowed` in the template, so a model whose
     * permission key is missing from the store took the whole page down with a
     * render error instead of showing the "no permission" card.
     */
    canBrowse() {
      return this.hasPermission(this.model.permissionKeys.browse);
    },
    canAdd() {
      return Boolean(this.model.add) && this.hasPermission(this.model.permissionKeys.add);
    },
    /**
     * Toolbar tools are opt-OUT: a model says `enableUpload: false` /
     * `enableExport: false` under `browse.tools` to drop the button, and every
     * model that says nothing keeps what it has always had.
     */
    showUpload() {
      return this.browseTools.enableUpload !== false;
    },
    showExport() {
      return this.browseTools.enableExport !== false;
    },
    browseTools() {
      return (this.model.browse && this.model.browse.tools) || {};
    },
  },
  data: () => ({
    api: new Api(),
    breadcrumbTabulator: new BreadcrumbTabulator(),
    model: new Model(),
    service: new Service(),
    listAllViews: [],
    columns: [],
    conditions: [],
    selectedRows: [],
    selectedCustomColums: [],
    selectedAdvanceFilterConditions: [],
    selectedFilterConditions: [],
    paginationSize: 10,
    paginationSizeSelector: [10, 50, 100, 200, 500],
    modelKey: "",
    parentId: "",
    isShow: false,
    cColumnSort: [],
    displayRowCount: 10,
    isLoading: false,
    allResizeColumns: [],
    columnSelectionDisabledCount: 2,
    currentPageNo: 1,
    apiThatReturnedError: "",
    apiTrashedBrowseUrl: "",
    recordBrowseAll: false,
    recordBrowseAllApiUrl: "",
    recordBrowseOwn: false,
    recordBrowseOwnApiUrl: "",
    recordBrowseAssignedTo: false,
    recordBrowseAssignedToApiUrl: "",
    recordBrowseBelongsTo: false,
    recordBrowseBelongsToApiUrl: "",
    dataLoadErrorCode: "",
  }),
  created() {
    this.model.getByKey(this.$route.params.modelKey);
    this.modelKey = this.$route.params.modelKey;
    this.parentId = this.$route.params.parentId;

    this.columns = this.$_.filter(
      this.model.browse.table.headers,
      (headers) => {
        return headers;
      }
    );

    this.columnSelectionDisabledCount =
      this.model.browse.table.actions.columnSelectionDisabledCount;
    this.apiTrashedBrowseUrl = this.model.apiUrls.apiTrashedBrowseUrl;
    this.manageRoleBasedRecordAccessControll();

    var columnIndex = 0;
    for (var column of this.columns) {
      if (column.dataType == "modelParent") {
        var parentModel = new Model();
        parentModel.getByKey(column.modelKey);
        this.columns[columnIndex].formatter = (cell) => {
          var parent = cell.getRow().getData()[this.$_ucfirst(parentModel.key)];
          var parentLabel = parent.id;
          if (parent.name) parentLabel = parent.name;
          if (parentModel.column && parentModel.column.text)
            parentLabel = parent[parentModel.column.text];

          return `<span class="tbl-company-link">${parentLabel}</span>`;
        };

        this.columns[columnIndex].accessorDownload = (e, data) => {
          return data[this.$_ucfirst(parentModel.key)].name;
        };

        this.columns[columnIndex].cellClick = (e, cell) => {
          var parent = cell.getRow().getData()[this.$_ucfirst(parentModel.key)];
          this.$router.push({
            name: "PageServiceModelBreadTabulatorRead",
            params: {
              serviceKey: parentModel.serviceKey,
              modelKey: parentModel.key,
              modelId: parent.id,
            },
          });
        };
      }

      if (column.dataType == "date") {
        this.columns[columnIndex].formatter = (cell) => {
          var exDate = cell.getValue();
          if (exDate) {
            return this.$moment(new Date(exDate)).format("YYYY-MM-DD");
          }
        };
      }
      columnIndex++;
    }

    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

    this.breadcrumbTabulator.addForOperationBrowse({
      model: this.model,
      services: this.services,
    });
  },
  mounted() {
    const viewId = this.$route.query.viewId;

    if (viewId !== "all" && viewId !== "trashed") {
      this.getAllViews(parseInt(viewId, 10));
    } else {
      this.getAllViews(viewId);
    }
  },
  methods: {
    hasPermission(key) {
      const permission = this.rolePermission[key];
      return Boolean(permission && permission.isAllowed);
    },
    /**
     * Opens over the listing, the same way Edit does. The `/new` page is still
     * routed to from the legacy dev-model pages, and stays the fallback here if
     * the dialog somehow isn't mounted.
     */
    goToAdd() {
      if (!this.canAdd) return;
      if (this.$refs.addDialog) {
        this.$refs.addDialog.open({ modelKey: this.model.key });
        return;
      }
      this.$router.push({
        name: "PageServiceModelBreadTabulatorAdd",
        params: {
          serviceKey: this.model.serviceKey,
          modelKey: this.model.key,
        },
      });
    },
    /**
     * Re-read rather than patching the edited row in place: the API may
     * normalise what it stored, and a row can carry derived columns (counts,
     * timestamps) the form never sent.
     */
    onRecordUpdated() {
      if (this.$refs.tabulatorDataTable) this.$refs.tabulatorDataTable.refresh();
    },
    apiGetBrowseUrl() {
      var userUuid = this.auth.uuid;
      var teamUuid = this.team;
      var fallback = `${
        this.$service[this.service.key]
      }/v1/en/console/tabulator/model/${this.model.key}/browse/all`;

      if (this.recordBrowseAll) {
        return this.recordBrowseAllApiUrl
          ? `${this.$service[this.service.key]}${this.recordBrowseAllApiUrl}`
          : fallback;
      }

      if (this.recordBrowseOwn) {
        return this.recordBrowseOwnApiUrl
          ? `${this.$service[this.service.key]}${
              this.recordBrowseOwnApiUrl
            }/${userUuid}`
          : fallback;
      }

      if (this.recordBrowseAssignedTo) {
        return this.recordBrowseOwnApiUrl
          ? `${this.$service[this.service.key]}${
              this.recordBrowseOwnApiUrl
            }/${userUuid}`
          : fallback;
      }

      if (this.recordBrowseBelongsTo) {
        return this.recordBrowseBelongsToApiUrl
          ? `${this.$service[this.service.key]}${
              this.recordBrowseBelongsToApiUrl
            }/${teamUuid}/listByTeamUuid/${this.auth.uuid}`
          : fallback;
      }

      return "";
    },
    apiGetBrowseTrashUrl() {
      var userUuid = this.auth.uuid;
      var teamUuid = this.team;
      var fallback = `${
        this.$service[this.service.key]
      }/v1/en/console/tabulator/model/${this.model.key}/browse/trashed`;

      if (this.recordBrowseAll) {
        return this.apiTrashedBrowseUrl
          ? `${this.$service[this.service.key]}${this.apiTrashedBrowseUrl}`
          : fallback;
      }

      if (this.recordBrowseOwn || this.recordBrowseAssignedTo) {
        return this.recordBrowseOwnApiUrl
          ? `${this.$service[this.service.key]}${
              this.apiTrashedBrowseUrl
            }/${userUuid}`
          : fallback;
      }

      if (this.recordBrowseBelongsTo) {
        return this.recordBrowseBelongsToApiUrl
          ? `${this.$service[this.service.key]}${
              this.apiTrashedBrowseUrl
            }/${teamUuid}/listByTeamUuid/${this.auth.uuid}`
          : fallback;
      }

      return "";
    },
    // manage records based on the user role
    manageRoleBasedRecordAccessControll() {
      var roleBasedRecordAccessControll = this.model.recordAccessControl.browse;

      var manageAll = roleBasedRecordAccessControll.all;
      var manageAssignedTo = roleBasedRecordAccessControll.assignedTo;
      var manageBelongsTo = roleBasedRecordAccessControll.belongsTo;
      var manageOwn = roleBasedRecordAccessControll.own;
      var userRoles = this.auth.roles.map((role) => {
        return role.slug;
      });

      for (var role of userRoles) {
        if (manageAll.roles.includes(role)) {
          this.recordBrowseAll = true;
          this.recordBrowseAllApiUrl = manageAll.apiUrl;
        }

        if (manageAssignedTo.roles.includes(role)) {
          this.recordBrowseAssignedTo = true;
          this.recordBrowseAssignedToApiUrl = manageAssignedTo.apiUrl;
        }

        if (manageBelongsTo.ssoTeam.roles.includes(role)) {
          this.recordBrowseBelongsTo = true;
          this.recordBrowseBelongsToApiUrl = manageBelongsTo.ssoTeam.apiUrl;
        }

        if (manageOwn.roles.includes(role)) {
          this.recordBrowseOwn = true;
          this.recordBrowseOwnApiUrl = manageOwn.apiUrl;
        }
      }
    },
    setFiltersViaConditions(conditions) {
      this.$refs.filter.setFiltersViaConditions(conditions);
    },
    getConditions() {
      return this.conditions;
    },
    setConditions(conditions) {
      this.selectedFilterConditions = conditions;
      this.conditions = conditions;
      this.$refs.tabulatorDataTable.getFilterCondition(conditions);
    },
    applyColumnSelection(selectedColumns) {
      this.selectedCustomColums = selectedColumns;
      this.$refs.tabulatorDataTable.applyColumnSelection(selectedColumns);
    },
    getCurrentPageSize(pagesize) {
      this.displayRowCount = pagesize;
    },
    getCurrentSort(sorters) {
      this.cColumnSort = sorters.map((sorter) => {
        return {
          column: sorter.field,
          dir: sorter.dir,
        };
      });
    },
    getCurrentPageNumber(pageno) {
      this.currentPageNo = pageno;
    },
    /**
     * Dragged column widths, accumulated so "Save view" can persist them into
     * CrmView.manageColumnCustomWidth (BreadTabulatorViewLatest reads this
     * array straight off the prop).
     */
    getCurrentColumnWidth(resizedColumn) {
      const field = resizedColumn.getField();
      const width = resizedColumn.getWidth();
      const index = this.allResizeColumns.findIndex((item) => item.field === field);

      if (index !== -1) {
        this.allResizeColumns[index].width = width;
      } else {
        this.allResizeColumns.push({ field, width });
      }
    },
    getSelectedRows(data) {
      this.selectedRows = data;
    },
    getDataLoadErrorCode(errorCode) {
      this.dataLoadErrorCode = errorCode;
    },
    changeTableDetails(listAllViews, viewId) {
      if (viewId == "trashed") {
        this.$refs.tabulatorDataTable.callApiUrl(this.apiGetBrowseTrashUrl());
        this.$refs.selectColumn.showAllColumns([]);
        this.$refs.tabulatorDataTable.callSavedHeaderWidth([]);
        this.$refs.search.getFilterCondition([]);
        this.$refs.customView.changeSelectedTabStatus("trashed", 0);
      } else if (viewId == "all") {
        this.$refs.tabulatorDataTable.callSavedHeaderWidth([]);
        this.$refs.selectColumn.showAllColumns([]);
        this.$refs.search.getFilterCondition([]);
        this.$refs.customView.changeSelectedTabStatus("all", 1);
        this.setCurrentPageTo();
      } else {
        const tabIndex =
          listAllViews.indexOf(listAllViews.find((x) => x.id === viewId)) + 2;
        this.$refs.customView.changeSelectedTabStatus(viewId, tabIndex);
        const singleViewDetail = listAllViews.filter((item) => {
          return item.id === viewId;
        });

        if (singleViewDetail.length > 0) {
          if (
            JSON.parse(singleViewDetail[0].manageColumnCustomWidth).length > 0
          ) {
            this.$refs.tabulatorDataTable.callSavedHeaderWidth(
              JSON.parse(singleViewDetail[0].manageColumnCustomWidth)
            );
          }
          this.$refs.selectColumn.showViewSelectedColumns(
            JSON.parse(singleViewDetail[0].cColumnCondition)
          );

          if (JSON.parse(singleViewDetail[0].filterCondition).length > 0) {
            this.$refs.filter.setFiltersViaConditions(
              JSON.parse(singleViewDetail[0].filterCondition)
            );
          }

          if (JSON.parse(singleViewDetail[0].cColumnSort).length > 0) {
            this.$refs.tabulatorDataTable.callSavedColumnSort(
              JSON.parse(singleViewDetail[0].cColumnSort)
            );
          }

          if (singleViewDetail[0].displayRowCount != this.paginationSize) {
            this.$refs.tabulatorDataTable.callSavedPageSize(
              singleViewDetail[0].displayRowCount
            );
          }
          // filter records only in the specific view tab
          this.$refs.search.getFilterCondition(
            JSON.parse(singleViewDetail[0].filterCondition)
          );
        }
        this.setCurrentPageTo();
      }
    },
    getAllViews(viewId) {
      this.api.setMethod(`GET`);
      this.api.setUrl(
        `${this.$service.hrdc}/v1/en/console/crmView/list?modelKey=${this.model.key}`
      );

      // The table's initial auto-load is deferred (deferInitialLoad) and is only
      // released once a view is applied below. If the view list fails to load,
      // release it explicitly so the table isn't stuck loading forever.
      this.api.setCallbackError(() => {
        this.apiThatReturnedError = this.api;
        if (this.$refs.tabulatorDataTable) {
          this.$refs.tabulatorDataTable.releaseDeferredLoad();
        }
      });

      this.api.setCallbackCompleted((response) => {
        const privateViews = response.filter((view) => {
          return view.isPrivate === true && view.ownerUuid === this.auth.uuid;
        });

        const publicViews = response.filter((view) => {
          return view.isPrivate === false;
        });

        this.listAllViews = [...publicViews, ...privateViews];
        this.changeTableDetails(this.listAllViews, viewId);
      });
      this.api.fetch();
    },
    setCurrentPageTo() {
      var pageNo = this.$route.query.page || 1;
      this.$refs.tabulatorDataTable.setCurrentPagNumber(pageNo);
    },
    downloadData(fileType, rowsType) {
      if (rowsType == "selected" && this.selectedRows.length == 0) {
        this.$store.dispatch("showMessage", {
          message: "Please select at least one row to download",
          messageType: "info",
          timeout: 2000,
        });
        return;
      }

      this.isLoading = true;
      this.$refs.tabulatorDataTable.exportData(fileType, rowsType, {
        filename: `${this.model.key}.${fileType}`,
        sheetName: `${this.model.key}`,
        title: this.model.name.plural,
      });
      this.$refs.tabulatorDataTable.clearSelection();
      this.isLoading = false;
    },
  },
};
</script>

<style scoped>
.rv-page {
  background-color: #d8dce2;
  font-family: "Lato", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
}

.rv-card-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.rv-denied {
  display: flex;
  align-items: center;
  padding: 16px 18px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
}

/* Page toolbar */
.page-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 16px 12px;
  flex-wrap: wrap;
  gap: 12px;
}

.page-toolbar__left {
  display: flex;
  align-items: baseline;
  gap: 12px;
  min-width: 0;
}

.page-toolbar__title {
  font-family: "Lato", sans-serif;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.page-toolbar__count {
  font-family: "Lato", sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  color: #94a3b8;
  white-space: nowrap;
}

.page-toolbar__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.page-search {
  padding: 0 16px 16px;
}

/* Filter bar */
.filter-bar {
  padding: 0 16px 12px;
}

.filter-bar__toggle {
  display: inline-flex;
  align-items: center;
  padding: 7px 14px;
  border-radius: 8px;
  font-family: "Lato", sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.15s ease;
}

.filter-bar__toggle:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.filter-bar__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 10px;
  background: #0ea5e9;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  margin-left: 6px;
}

/* Filter panel */
.filter-panel {
  margin: 0 16px 14px;
  padding: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.filter-panel__columns {
  background: #f8fafc;
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
}

/* Override BreadTabulatorSelectColumn styles */
.filter-panel__columns >>> .tb-right-section {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-panel__columns >>> .tb-show-column {
  display: none !important;
}

.filter-panel__columns >>> .tb-select-column-dropdown {
  flex: 1;
  min-width: 200px;
}

.filter-panel__columns >>> .tb-select-column-dropdown .v-input {
  margin-top: 0 !important;
  padding-top: 0 !important;
  border: none !important;
  border-radius: 8px !important;
  background: transparent !important;
}

.filter-panel__columns >>> .tb-select-column-dropdown .v-input__slot {
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  min-height: 38px !important;
  box-shadow: none !important;
  padding: 0 8px !important;
}

.filter-panel__columns >>> .tb-select-column-dropdown .v-input__slot::before,
.filter-panel__columns >>> .tb-select-column-dropdown .v-input__slot::after {
  display: none !important;
}

.filter-panel__columns >>> .tb-select-column-dropdown .v-input__slot:hover {
  border-color: #cbd5e1 !important;
}

.filter-panel__columns >>> .tb-select-column-dropdown .v-input__control {
  min-height: 38px !important;
}

.filter-panel__columns >>> .tb-select-column-dropdown .v-input__control > .v-input__slot {
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  background: #fff !important;
}

.filter-panel__columns >>> .tb-select-column-dropdown .v-text-field__details {
  display: none !important;
}

.filter-panel__columns >>> .tb-select-column-dropdown .v-select__selection {
  font-family: "Lato", sans-serif !important;
  font-size: 0.78rem !important;
  font-weight: 600 !important;
  color: #334155 !important;
}

.filter-panel__columns >>> .tb-select-column-dropdown .v-chip {
  background: #e0f2fe !important;
  color: #0369a1 !important;
  border-radius: 6px !important;
  font-family: "Lato", sans-serif !important;
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  height: 24px !important;
}

.filter-panel__columns >>> .tb-select-column-dropdown .grey--text {
  font-family: "Lato", sans-serif !important;
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  color: #64748b !important;
}

.filter-panel__columns >>> .tb-select-column-dropdown .v-label {
  font-family: "Lato", sans-serif !important;
  font-size: 0.82rem !important;
  color: #94a3b8 !important;
}

.filter-panel__columns >>> .tb-clear-btn {
  font-family: "Lato", sans-serif !important;
  font-size: 0.78rem !important;
  font-weight: 600 !important;
  color: #64748b !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  padding: 0 14px !important;
  min-width: auto !important;
  height: 38px !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  background: #fff !important;
}

.filter-panel__columns >>> .tb-clear-btn:hover {
  background: #f1f5f9 !important;
  border-color: #cbd5e1 !important;
}

/* Override BreadTabulatorFilterable styles */
.filter-panel >>> .pb-3 {
  background: #f8fafc;
  padding: 14px 16px 14px !important;
  border-bottom: 1px solid #e2e8f0;
}

.filter-panel >>> .row:not(.pb-3) {
  padding: 12px 16px !important;
}

.filter-panel >>> .v-input__slot {
  background: #fff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  min-height: 36px !important;
  box-shadow: none !important;
}

.filter-panel >>> .v-input__slot::before,
.filter-panel >>> .v-input__slot::after {
  display: none !important;
}

.filter-panel >>> .v-input__slot:hover {
  border-color: #cbd5e1 !important;
}

.filter-panel >>> .v-select__selection,
.filter-panel >>> .v-text-field__slot input,
.filter-panel >>> .v-label {
  font-family: "Lato", sans-serif !important;
  font-size: 0.82rem !important;
}

.filter-panel >>> .v-select__selection {
  font-weight: 600 !important;
  color: #334155 !important;
}

.filter-panel >>> .v-label {
  color: #94a3b8 !important;
}

.filter-panel >>> .v-text-field__slot input {
  font-weight: 600 !important;
  color: #1a1e24 !important;
}

.filter-panel >>> .tb-btn {
  font-family: "Lato", sans-serif !important;
  font-size: 0.78rem !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  border-radius: 8px !important;
  min-width: auto !important;
  padding: 0 18px !important;
  height: 36px !important;
  box-shadow: none !important;
  background: #0ea5e9 !important;
  color: #fff !important;
}

.filter-panel >>> .tb-btn:hover {
  background: #0284c7 !important;
}

.filter-panel >>> .tb-btn-clear {
  font-family: "Lato", sans-serif !important;
  font-size: 0.78rem !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  border-radius: 8px !important;
  min-width: auto !important;
  padding: 0 18px !important;
  height: 36px !important;
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
  background: #fff !important;
  color: #64748b !important;
}

.filter-panel >>> .tb-btn-clear:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
}

.filter-panel >>> .tb-filter-applied {
  display: flex;
  align-items: center;
}

.filter-panel >>> .tb-filter-applied h4 {
  font-family: "Lato", sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
  white-space: nowrap;
}

.filter-panel >>> .v-chip {
  border-radius: 6px !important;
  font-family: "Lato", sans-serif !important;
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  background: #fef3c7 !important;
  color: #92400e !important;
}

.filter-panel >>> .with-border .v-input__slot {
  border: 1px solid #e2e8f0 !important;
}

/* New record button */
.new-app-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  border-radius: 10px;
  border: none;
  background: #0ea5e9;
  color: #fff;
  font-family: "Lato", sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  margin-right: 8px;
}

.new-app-btn:hover:not([disabled]) {
  background: #0284c7;
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.new-app-btn[disabled] {
  background: #cbd5e1;
  cursor: default;
}

/* Responsive */
@media (max-width: 959px) {
  .page-toolbar {
    padding: 16px 12px 10px;
  }

  .page-toolbar__title {
    font-size: 1rem;
  }

  .page-toolbar__left {
    flex-direction: column;
    gap: 4px;
  }

  .page-search {
    padding: 0 12px 12px;
  }

  .filter-bar {
    padding: 0 12px 10px;
  }

  .filter-panel__columns >>> .tb-select-column-dropdown {
    min-width: 0;
    width: 100%;
  }

  .filter-panel {
    margin: 0 12px 10px;
  }

  .filter-panel >>> .row {
    flex-direction: column;
  }

  .filter-panel >>> .col-md-4,
  .filter-panel >>> .col-md-3,
  .filter-panel >>> .col-md-2,
  .filter-panel >>> .col-md-9,
  .filter-panel >>> .col-md-1 {
    max-width: 100% !important;
    flex: 0 0 100% !important;
    padding: 2px 0 !important;
  }
}

@media (max-width: 599px) {
  .rv-page {
    padding: 8px !important;
  }

  .rv-card-wrapper {
    border-radius: 10px;
  }

  .page-toolbar {
    padding: 12px 10px 8px !important;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .page-toolbar__title {
    font-size: 0.95rem !important;
  }

  .page-toolbar__right {
    width: 100%;
    justify-content: flex-end;
  }

  .filter-bar {
    padding: 0 10px 8px !important;
  }

  .filter-bar__toggle {
    font-size: 0.78rem !important;
    padding: 6px 12px !important;
  }

  .filter-panel {
    margin: 0 10px 8px !important;
  }

  .page-search {
    padding: 0 10px 10px !important;
  }
}

/* ============================================
   DARK MODE — SCOPED
   Token references only (--app-*, defined in src/plugins/theme-dark.css).
   Never paste a literal hex back into this block.
   ============================================ */
.dark-mode .rv-page {
  background-color: var(--app-page);
}

.dark-mode .rv-card-wrapper,
.dark-mode .rv-card-wrapper.v-card {
  background: var(--app-card) !important;
}

.dark-mode .rv-denied {
  background: var(--app-danger-bg);
  border-color: var(--app-border);
}

.dark-mode .page-toolbar__title {
  color: var(--app-text);
}

.dark-mode .page-toolbar__count {
  color: var(--app-text-faint);
}

.dark-mode .filter-bar__toggle {
  color: var(--app-text);
  background: var(--app-page);
  border-color: var(--app-border);
}

.dark-mode .filter-bar__toggle:hover {
  background: var(--app-card);
  border-color: var(--app-border-strong);
}

.dark-mode .filter-panel {
  background: var(--app-card);
  border-color: var(--app-border);
}

.dark-mode .filter-panel__columns {
  background: var(--app-page);
  border-color: var(--app-border);
}

.dark-mode .filter-panel >>> .pb-3 {
  background: var(--app-page);
  border-bottom-color: var(--app-border);
}

.dark-mode .filter-panel >>> .v-input__slot {
  background: var(--app-page) !important;
  border-color: var(--app-border) !important;
}

.dark-mode .filter-panel >>> .v-input__slot:hover {
  border-color: var(--app-border-strong) !important;
}

.dark-mode .filter-panel >>> .v-select__selection,
.dark-mode .filter-panel >>> .v-text-field__slot input {
  color: var(--app-text) !important;
}

.dark-mode .filter-panel >>> .v-label {
  color: var(--app-text-faint) !important;
}

.dark-mode .filter-panel >>> .tb-btn {
  background: var(--app-accent) !important;
}

.dark-mode .filter-panel >>> .tb-btn-clear {
  background: var(--app-card) !important;
  border-color: var(--app-border) !important;
  color: var(--app-text-muted) !important;
}

.dark-mode .filter-panel >>> .tb-filter-applied h4 {
  color: var(--app-text-faint);
}

.dark-mode .filter-panel >>> .v-chip {
  background: var(--app-warning-bg) !important;
  color: var(--app-warning-fg) !important;
}

.dark-mode .filter-panel__columns >>> .tb-select-column-dropdown .v-input__slot,
.dark-mode .filter-panel__columns >>> .tb-select-column-dropdown .v-input__control > .v-input__slot {
  background: var(--app-page) !important;
  border-color: var(--app-border) !important;
}

.dark-mode .filter-panel__columns >>> .tb-select-column-dropdown .v-select__selection {
  color: var(--app-text) !important;
}

.dark-mode .filter-panel__columns >>> .tb-select-column-dropdown .v-chip {
  background: var(--app-accent-soft) !important;
  color: var(--app-accent) !important;
}

.dark-mode .filter-panel__columns >>> .tb-select-column-dropdown .grey--text {
  color: var(--app-text-muted) !important;
}

.dark-mode .filter-panel__columns >>> .tb-select-column-dropdown .v-label {
  color: var(--app-text-faint) !important;
}

.dark-mode .filter-panel__columns >>> .tb-clear-btn {
  background: var(--app-card) !important;
  border-color: var(--app-border) !important;
  color: var(--app-text-muted) !important;
}

.dark-mode .new-app-btn[disabled] {
  background: var(--app-border-strong);
  color: var(--app-text-faint);
}
</style>

<style>
/* Unscoped: the view-tab strip and the toolbar button group render markup this
   page does not own, and Vuetify moves menu content outside the component. */

/* View tabs bar */
.rv-page .tb-view-area {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-bottom: none;
  border-radius: 10px 10px 0 0;
  margin: 0;
  align-items: center;
}

.rv-page .tb-view-area .v-tabs {
  background: transparent !important;
}

.rv-page .tb-view-area .v-tab {
  font-family: "Lato", sans-serif !important;
  font-size: 0.78rem !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  color: #64748b !important;
  min-width: auto !important;
  padding: 0 14px !important;
}

.rv-page .tb-view-area .v-tab:hover {
  color: #334155 !important;
  background: #f1f5f9 !important;
}

.rv-page .tb-view-area .v-tab--active {
  color: #0369a1 !important;
  background: #fff !important;
}

.rv-page .tb-view-area .v-tabs-slider-wrapper {
  color: #0ea5e9 !important;
}

/* List menu button in the tab strip */
.rv-page .tb-view-area > .col-md-10 > .v-btn {
  background: #fff !important;
  border: none !important;
  border-right: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  min-width: 40px !important;
  width: 40px !important;
  padding: 0 !important;
  border-radius: 10px 0 0 0 !important;
}

.rv-page .tb-view-area > .col-md-10 > .v-btn .v-icon {
  color: #64748b !important;
  font-size: 20px !important;
}

.rv-page .tb-view-area > .col-md-10 > .v-btn:hover {
  background: #f1f5f9 !important;
}

.rv-page .tb-view-area .v-slide-group__prev,
.rv-page .tb-view-area .v-slide-group__next {
  min-width: 28px !important;
}

.rv-page .tb-view-area .v-slide-group__prev .v-icon,
.rv-page .tb-view-area .v-slide-group__next .v-icon {
  color: #64748b !important;
}

.rv-page .tb-view-area .tb-view-dropdown-btn .v-btn {
  min-width: auto !important;
  width: 20px !important;
  height: 20px !important;
  padding: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

.rv-page .tb-view-area .tb-view-dropdown-btn .mdi-chevron-down {
  font-size: 16px !important;
  color: #94a3b8 !important;
}

/* Upload / Export buttons in the toolbar */
.rv-page .page-toolbar__right .v-btn-toggle .v-btn--tile {
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-family: "Lato", sans-serif !important;
  font-size: 0.82rem !important;
  font-weight: 600 !important;
  letter-spacing: normal !important;
  color: #334155 !important;
  background: #fff !important;
  padding: 0 16px !important;
  height: 36px !important;
  transition: all 0.15s ease;
}

.rv-page .page-toolbar__right .v-btn-toggle .v-btn--tile:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
}

.rv-page .page-toolbar__right .v-btn-toggle .v-btn--tile .v-icon {
  color: #64748b !important;
  font-size: 18px !important;
}

.rv-page .page-toolbar__right .v-btn-toggle {
  border-radius: 8px !important;
  box-shadow: none !important;
}

/* New View / Save buttons rendered by BreadTabulatorViewLatest */
.rv-page .col-md-2 .v-btn--tile.v-btn--outlined {
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-family: "Lato", sans-serif !important;
  font-size: 0.78rem !important;
  font-weight: 600 !important;
  letter-spacing: normal !important;
  color: #334155 !important;
  background: #fff !important;
  padding: 0 16px !important;
  height: 34px !important;
  min-width: auto !important;
  margin: 0 3px !important;
  transition: all 0.15s ease;
}

.rv-page .col-md-2 .v-btn--tile.v-btn--outlined:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
}

.rv-page .col-md-2 .v-btn--tile.v-btn--outlined.v-btn--disabled {
  opacity: 0.4 !important;
  color: #94a3b8 !important;
}

@media (max-width: 959px) {
  .rv-page .tb-view-area {
    border-radius: 8px 8px 0 0;
  }

  .rv-page .tb-view-area .v-slide-group__wrapper {
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch;
  }

  .rv-page .tb-view-area .v-tab {
    font-size: 0.72rem !important;
    padding: 0 10px !important;
  }

  .rv-page .tb-view-area .tb-view-dropdown-btn {
    font-size: 0.72rem !important;
    padding: 0 8px !important;
    min-width: auto !important;
  }
}

@media (max-width: 599px) {
  .rv-page .tb-view-area {
    border-radius: 0;
    padding: 0 4px;
  }

  .rv-page .tb-view-area .v-tab {
    font-size: 0.68rem !important;
    padding: 0 8px !important;
    min-width: auto !important;
  }
}

/* ============================================
   DARK MODE — UNSCOPED
   ============================================ */
.dark-mode .rv-page .tb-view-area {
  background: var(--app-page) !important;
  border-color: var(--app-border) !important;
}

.dark-mode .rv-page .tb-view-area .v-tabs,
.dark-mode .rv-page .tb-view-area .v-tabs-bar,
.dark-mode .rv-page .tb-view-area .v-slide-group__wrapper,
.dark-mode .rv-page .tb-view-area .v-slide-group__content,
.dark-mode .rv-page .tb-view-area .col-md-2 {
  background: transparent !important;
}

.dark-mode .rv-page .tb-view-area .v-slide-group__prev,
.dark-mode .rv-page .tb-view-area .v-slide-group__next {
  background: var(--app-page) !important;
}

.dark-mode .rv-page .tb-view-area .v-slide-group__prev .v-icon,
.dark-mode .rv-page .tb-view-area .v-slide-group__next .v-icon {
  color: var(--app-text-muted) !important;
}

.dark-mode .rv-page .tb-view-area .v-tab {
  color: var(--app-text-muted) !important;
  background: transparent !important;
}

.dark-mode .rv-page .tb-view-area .v-tab:hover {
  color: var(--app-text) !important;
  background: var(--app-card) !important;
}

.dark-mode .rv-page .tb-view-area .v-tab--active {
  color: var(--app-accent) !important;
  background: var(--app-card) !important;
}

/* The strip behind this button is --app-page, so --app-surface keeps it
   reading as a raised control at the same step size as the light pair
   (#fff on #f8fafc) rather than jumping a full tier. */
.dark-mode .rv-page .tb-view-area > .col-md-10 > .v-btn {
  background: var(--app-surface) !important;
  border-color: var(--app-border) !important;
}

.dark-mode .rv-page .tb-view-area > .col-md-10 > .v-btn .v-icon {
  color: var(--app-text-muted) !important;
}

.dark-mode .rv-page .tb-view-area > .col-md-10 > .v-btn:hover {
  background: var(--app-elevated) !important;
}

.dark-mode .rv-page .tb-view-area .tb-view-dropdown-btn .mdi-chevron-down {
  color: var(--app-text-faint) !important;
}

.dark-mode .rv-page .page-toolbar__right .v-btn-toggle {
  background: transparent !important;
}

.dark-mode .rv-page .page-toolbar__right .v-btn-toggle .v-btn--tile {
  background: var(--app-card) !important;
  border-color: var(--app-border) !important;
  color: var(--app-text) !important;
}

.dark-mode .rv-page .page-toolbar__right .v-btn-toggle .v-btn--tile:hover {
  background: var(--app-surface) !important;
  border-color: var(--app-border-strong) !important;
}

.dark-mode .rv-page .page-toolbar__right .v-btn-toggle .v-btn--tile .v-icon {
  color: var(--app-text-muted) !important;
}

.dark-mode .rv-page .col-md-2 .v-btn--tile.v-btn--outlined {
  background: var(--app-card) !important;
  border-color: var(--app-border) !important;
  color: var(--app-text) !important;
}

.dark-mode .rv-page .col-md-2 .v-btn--tile.v-btn--outlined:hover {
  background: var(--app-surface) !important;
  border-color: var(--app-border-strong) !important;
}

.dark-mode .rv-page .col-md-2 .v-btn--tile.v-btn--outlined.v-btn--disabled {
  color: var(--app-text-faint) !important;
}

/* Search bar (BreadTabulatorSearchable) */
.dark-mode .rv-page .search-select .v-input__slot,
.dark-mode .rv-page .search-input .v-input__slot {
  background: var(--app-page) !important;
  border-color: var(--app-border) !important;
}

.dark-mode .rv-page .search-select .v-select__selection,
.dark-mode .rv-page .search-input input {
  color: var(--app-text) !important;
}

.dark-mode .rv-page .search-select .v-label,
.dark-mode .rv-page .search-input .v-label {
  color: var(--app-text-faint) !important;
}

.dark-mode .rv-page .search-bar__btn--clear {
  background: var(--app-card) !important;
  border-color: var(--app-border) !important;
  color: var(--app-text-muted) !important;
}
</style>
